// src/components/hooks/useVoiceSettings.ts
import { useEffect, useMemo, useState } from "react";
import {
  getSpeakMode,
  getVoiceEnabled,
  getVoiceProvider,
  getVoiceSettingsSnapshot,
  setSpeakMode as persistSpeakMode,
  setVoiceEnabled as persistVoiceEnabled,
  setVoiceProvider as persistVoiceProvider,
  subscribeVoiceSettings,
  type SpeakMode,
  type VoiceProvider,
} from "@/lib/speech/voiceSettings";

export type UseVoiceSettingsResult = {
  voiceEnabled: boolean;
  voiceProvider: VoiceProvider;
  speakMode: SpeakMode;

  speakAnswers: boolean;
  canSpeakHelp: boolean;

  setVoiceEnabled: (v: boolean) => void;
  setVoiceProvider: (v: VoiceProvider) => void;
  setSpeakMode: (v: SpeakMode) => void;
};

export function useVoiceSettings(): UseVoiceSettingsResult {
  const [voiceEnabled, setVoiceEnabledState] = useState<boolean>(() => getVoiceEnabled());
  const [voiceProvider, setVoiceProviderState] = useState<VoiceProvider>(() => getVoiceProvider());
  const [speakMode, setSpeakModeState] = useState<SpeakMode>(() => getSpeakMode());

  // Initial sync + subscription
  useEffect(() => {
    const snap = getVoiceSettingsSnapshot();

    /* eslint-disable react-hooks/set-state-in-effect */
    setVoiceEnabledState((prev) => (prev === snap.enabled ? prev : snap.enabled));
    setVoiceProviderState((prev) => (prev === snap.provider ? prev : snap.provider));
    setSpeakModeState((prev) => (prev === snap.speakMode ? prev : snap.speakMode));
    /* eslint-enable react-hooks/set-state-in-effect */

    return subscribeVoiceSettings((next) => {
      setVoiceEnabledState((prev) => (prev === next.enabled ? prev : next.enabled));
      setVoiceProviderState((prev) => (prev === next.provider ? prev : next.provider));
      setSpeakModeState((prev) => (prev === next.speakMode ? prev : next.speakMode));
    });
  }, []);

  const setVoiceEnabled = (v: boolean) => {
    persistVoiceEnabled(v);
  };

  const setVoiceProvider = (v: VoiceProvider) => {
    persistVoiceProvider(v);
  };

  const setSpeakMode = (v: SpeakMode) => {
    persistSpeakMode(v);
  };

  const speakAnswers = useMemo(
    () => speakMode === "answers" || speakMode === "all",
    [speakMode]
  );

  const canSpeakHelp = useMemo(
    () => voiceEnabled && (speakMode === "help" || speakMode === "all"),
    [voiceEnabled, speakMode]
  );

  return {
    voiceEnabled,
    voiceProvider,
    speakMode,
    speakAnswers,
    canSpeakHelp,
    setVoiceEnabled,
    setVoiceProvider,
    setSpeakMode,
  };
}